import { icons as iconTokens } from 'src/theme/tokens.generated'

// ----------------------------------------------------------------------
// One glyph per verb, and one glyph per status.
//
// A verb binds to a glyph once, so Edit carries the same mark in a row action,
// a panel header and a record header, and nothing else in the product uses that
// mark for anything else. The bindings live in the token source, so a rename is
// a one line change there rather than a sweep through components.
//
// The one documented exception is Rentals, `mdi:car-multiple`: neither Lucide
// nor Tabler ships a two car glyph and a single car already means Vehicle, so
// Rentals would have collided with Vehicles in the sidebar. Adding a second
// exception requires replacing this one.
// ----------------------------------------------------------------------

export type VerbName = keyof typeof iconTokens.verbs

/** `icons.edit` is the only way to draw a pencil. */
export const icons = Object.fromEntries(
  Object.entries(iconTokens.verbs).map(([verb, entry]) => [verb, entry.icon]),
) as Record<VerbName, string>

export type SectionName = keyof typeof iconTokens.sections

/**
 * One glyph per top-level console destination.
 *
 * A separate map from `icons` because a section is a place you go and a verb is
 * a thing you do, and the two may share a subject without sharing a glyph. The
 * nav spelled all eight of its own strings before this, which is a rename in
 * eight places and a miss in one of them.
 */
export const sectionIcons = Object.fromEntries(
  Object.entries(iconTokens.sections).map(([name, entry]) => [
    name,
    entry.icon,
  ]),
) as Record<SectionName, string>

/**
 * The mark a yes or a no takes in a table cell: a tick and a dash, from the
 * token source. `true` spelled down a column is read letter by letter, and
 * beside a column of `false` it is two columns of the same grey shape.
 */
export const answerGlyph = (value: boolean): string =>
  value ? iconTokens.answer.yes : iconTokens.answer.no

/**
 * The mark beside a sum in this currency, from the token source. A currency
 * with no mark of its own takes the banknote, so it still reads as money.
 */
export const currencyGlyph = (currency: string): string => {
  const marks: Record<string, string> = iconTokens.unit.currency
  return marks[currency] ?? iconTokens.unit.currency.default
}

/** The mark beside a percentage. */
export const percentageGlyph: string = iconTokens.unit.percentage

/**
 * The mark of what a renter added to a booking, from its code's family, the
 * part before the dot: `deductible.minus70percent` takes the deductible's.
 * A family the token source does not name takes the default, never no mark.
 */
export const addonGlyph = (code: string): string => {
  const marks: Record<string, string> = iconTokens.addon
  return marks[code.split('.')[0] ?? ''] ?? iconTokens.addon.default
}

/**
 * The mark of Beast Bucks, wherever they are named: a balance, the Balance
 * tab, a rental's Bucks movements. Drawn in the Beast pack, a token reading
 * BB, because no pack has a mark for a fleet's own credit, and the card and
 * the bank it borrowed were a payment and a bank account.
 */
export const BEAST_BUCKS_GLYPH: string = iconTokens.unit.beastBucks

/**
 * The five-step optical grid: 14 in a chip, 16 in a dense cell or key-value
 * row, 18 on a 40px control, 20 for a standalone toolbar action, 24 for a page
 * level action, section head or command glyph. The size is chosen by context,
 * never by eye; `Iconify` takes one of these names as `size`.
 */
export const iconSize = iconTokens.sizes

export type IconSizeName = keyof typeof iconSize

/**
 * Rendered stroke in device pixels per optical size, plus `solo` for a glyph
 * that is the only content of a control. Both packs draw on a 24 unit grid
 * with a 2 unit stroke, so without this a 14px glyph puts down 1.17px where a
 * 24px glyph puts down 2px, which is the drift that makes a toolbar look hand
 * assembled. `Iconify` scales `stroke-width` by grid over size so the rendered
 * weight is what this table says.
 */
export const iconStroke = iconTokens.strokes

/** The unit grid both icon packs are drawn on. */
export const ICON_GRID = iconTokens.grid

/**
 * Per glyph optical size, as a fraction of the step the glyph is drawn at.
 *
 * A named step fixes the box, not the mark. Both packs draw on the same 24
 * unit grid and every glyph fills a different amount of it, so the nine marks
 * the sidebar shows at one step paint between 15.7 and 21.7 units across. Most
 * of that spread is invisible, because what is read is not the bounding box: a
 * closed outline is read at its full extent and an open one at its parts,
 * which is why of the four largest marks in the nav it is the ring and the
 * case that are picked out while the route and the gear beside them, measuring
 * the same, are not.
 *
 * So this is a correction and not a normalisation. Values are written per
 * glyph in the token source, set by looking at a rendered column, and they
 * only ever shrink.
 */
const OPTICAL = iconTokens.optical.glyphs as Record<string, { scale: number }>

/**
 * How large a glyph is drawn relative to its step. `1` for everything without
 * a recorded correction, which is almost everything.
 */
export const opticalScale = (glyph: string | undefined): number =>
  (glyph !== undefined && OPTICAL[glyph]?.scale) || 1

// ----------------------------------------------------------------------
// Status glyphs
// ----------------------------------------------------------------------

type StatusGlyphTokens = Omit<typeof iconTokens.status, 'usedFor'>

/**
 * The domains that have a status glyph map. This is the same set of names the
 * palette keys its colour maps by, so a chip cannot get its colour from one
 * domain and its glyph from another.
 */
export type StatusDomain = keyof StatusGlyphTokens

const STATUS_GLYPHS = iconTokens.status as StatusGlyphTokens

/**
 * The domains whose values arrive from an API as an untyped string, so no type
 * can close them:
 *   * `subscription` carries Stripe's own status vocabulary, and the schema
 *     types the field as `String`.
 *
 * Everything else is closed and an unknown state is a compile error. These
 * narrow at runtime instead, onto the domain's `default` entry, which is a
 * documented state rather than a silent grey.
 *
 * A rental's feedback tags were the other open domain. They are tags, not
 * statuses, and are drawn as tags now (`TagChip`), with no glyph.
 */
export type OpenStatusDomain =
  | 'subscription'
  // Stripe's, arriving as an unconstrained string, so the console cannot
  // know at build time which of its states a given account is in.
  | 'providerSubscription'

/**
 * Every state in a domain, as a union. A value outside it fails type checking
 * rather than falling back to grey with no mark. `default` is the runtime
 * fallback and is not something a caller passes.
 */
export type StatusValue<D extends StatusDomain> = Exclude<
  keyof StatusGlyphTokens[D],
  'default'
> &
  string

/**
 * The glyph for a status. Colour never carries a status on its own: roughly one
 * operator in twelve cannot separate the red from the green, and a fleet is
 * scanned rather than read.
 */
export const statusGlyph = (domain: StatusDomain, value: string): string => {
  const map = STATUS_GLYPHS[domain] as Record<string, string>
  return map[value] ?? map.default
}

/**
 * Whether a status is describing something happening *now* rather than
 * something that happened.
 *
 * `Rented` is the case: a car wheel that does not roll says the car was
 * driven, and the whole point of the chip is that somebody is driving it. The
 * binding is in the token source beside the glyph and the colour, so a state
 * that moves cannot drift from the state that has a mark.
 *
 * `rental.started` is deliberately not here, and keeps the steering wheel. The
 * booking calendar draws it as a legend swatch, and a key that moves is a key
 * an operator reads as a state.
 */
const LIVE_STATUSES = iconTokens.statusLive as Record<
  string,
  readonly string[] | string
>

export const statusIsLive = (domain: StatusDomain, value: string): boolean => {
  const live = LIVE_STATUSES[domain]
  return Array.isArray(live) && live.includes(value)
}

export type GlyphTurn = 'clockwise' | 'counterclockwise'

/**
 * Which way a glyph turns while its subject is live.
 *
 * A mark is drawn pointing one way, and turned the other way it reads as
 * running backward: the Rented wheel's blades hook counterclockwise at the rim,
 * and turning it clockwise was reported as a car rolling in reverse. The
 * Refresh arrows point clockwise. So the direction is the glyph's, bound in the
 * token source beside its other drawing corrections, and a glyph with no entry
 * turns clockwise.
 */
const TURNS = iconTokens.turn as Record<string, GlyphTurn | undefined>

export const glyphTurn = (glyph: string): GlyphTurn =>
  TURNS[glyph] ?? 'clockwise'
